"use client";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "react-toastify";
import { useState } from "react";

const SOURCE_OPTIONS = [
  "Bouche à oreille",
  "YouTube",
  "Réseaux sociaux",
  "Recherche Google",
  "Article / Blog",
  "Publicité",
  "Autre",
];

const OBJECTIVE_OPTIONS = [
  "Reconversion professionnelle",
  "Montée en compétences",
  "Lancer un projet / startup",
  "Trouver un emploi dans la tech",
  "Curiosité / Apprentissage personnel",
  "Autre",
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[\d\s\-().]{10,14}$/;

function validateEmail(email) {
  return EMAIL_REGEX.test(email);
}

function validatePhone(phone) {
  const digits = phone.replace(/[\s\-().]/g, "");
  return PHONE_REGEX.test(phone) && digits.replace("+", "").length >= 10;
}

function ProgressDots({ currentStep, totalSteps }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div
          key={i}
          className={`h-2 rounded-full transition-all duration-300 ${
            i + 1 === currentStep
              ? "w-8 bg-blue-500"
              : i + 1 < currentStep
                ? "w-2 bg-blue-500"
                : "w-2 bg-gray-200 dark:bg-grayDark-700"
          }`}
        />
      ))}
    </div>
  );
}

export default function AcceleratorLeadModal({
  open,
  onOpenChange,
  accelerator = "rocket",
  calLink = "#",
}) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState("");
  const [sourceOther, setSourceOther] = useState("");

  const [objective, setObjective] = useState("");
  const [objectiveOther, setObjectiveOther] = useState("");
  const [motivation, setMotivation] = useState("");

  function resetForm() {
    setStep(1);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setSource("");
    setSourceOther("");
    setObjective("");
    setObjectiveOther("");
    setMotivation("");
    setIsSubmitting(false);
    setShowSuccess(false);
  }

  function handleOpenChange(value) {
    if (!value) {
      resetForm();
    }
    onOpenChange(value);
  }

  function validateStep1() {
    if (!firstName.trim()) {
      toast.error("Veuillez renseigner votre prénom.");
      return false;
    }
    if (!lastName.trim()) {
      toast.error("Veuillez renseigner votre nom.");
      return false;
    }
    if (!email.trim() || !validateEmail(email)) {
      toast.error("Veuillez renseigner un email valide.");
      return false;
    }
    if (!phone.trim() || !validatePhone(phone)) {
      toast.error("Veuillez renseigner un numéro de téléphone valide.");
      return false;
    }
    if (!source) {
      toast.error("Veuillez indiquer comment vous avez entendu parler de nous.");
      return false;
    }
    if (source === "Autre" && !sourceOther.trim()) {
      toast.error("Veuillez préciser votre réponse.");
      return false;
    }
    return true;
  }

  function validateStep2() {
    if (!objective) {
      toast.error("Veuillez sélectionner votre objectif principal.");
      return false;
    }
    if (objective === "Autre" && !objectiveOther.trim()) {
      toast.error("Veuillez préciser votre objectif.");
      return false;
    }
    return true;
  }

  function handleContinueStep1() {
    if (validateStep1()) {
      setStep(2);
    }
  }

  function handleContinueStep2() {
    if (validateStep2()) {
      setStep(3);
    }
  }

  async function submitLead(contactPreference) {
    setIsSubmitting(true);
    try {
      const response = await fetch("/api/accelerators/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          source: source === "Autre" ? `Autre : ${sourceOther.trim()}` : source,
          objective: objective === "Autre" ? `Autre : ${objectiveOther.trim()}` : objective,
          motivation: motivation.trim(),
          contactPreference,
          accelerator,
        }),
      });

      if (!response.ok) {
        throw new Error("Erreur lors de l'envoi du formulaire.");
      }

      return true;
    } catch (error) {
      toast.error(
        error.message || "Une erreur est survenue. Veuillez réessayer."
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCallBack() {
    const success = await submitLead("callback");
    if (success) {
      setShowSuccess(true);
    }
  }

  async function handleBookSlot() {
    const success = await submitLead("booking");
    if (success) {
      window.open(calLink, "_blank", "noopener,noreferrer");
      handleOpenChange(false);
    }
  }

  const inputClasses =
    "w-full rounded-lg border border-gray-300 dark:border-grayDark-600 bg-white dark:bg-grayDark-800 px-4 py-3 text-sm text-gray-900 dark:text-grayDark-100 placeholder-gray-400 dark:placeholder-grayDark-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-hidden transition-colors";

  const selectClasses =
    "w-full rounded-lg border border-gray-300 dark:border-grayDark-600 bg-white dark:bg-grayDark-800 px-4 py-3 text-sm text-gray-900 dark:text-grayDark-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-hidden transition-colors appearance-none cursor-pointer";

  const labelClasses =
    "block text-sm font-medium text-gray-700 dark:text-grayDark-300 mb-1.5";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`${
          step === 3 || showSuccess ? "sm:max-w-2xl" : "sm:max-w-lg"
        } max-h-[90vh] overflow-y-auto p-6 sm:p-8`}
      >
        {/* Success State */}
        {showSuccess ? (
          <div className="flex flex-col items-center text-center py-6">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-6 animate-[scaleIn_0.3s_ease-out]">
              <svg
                className="w-8 h-8 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                  className="animate-[drawCheck_0.4s_ease-out_0.2s_both]"
                  style={{
                    strokeDasharray: 24,
                    strokeDashoffset: 24,
                    animation: "drawCheck 0.4s ease-out 0.2s forwards",
                  }}
                />
              </svg>
            </div>

            <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              Merci {firstName} !
            </DialogTitle>

            <p className="text-gray-600 dark:text-grayDark-400 mb-0 max-w-sm">
              Notre équipe va vous contacter très rapidement. Vous allez aussi
              recevoir un email de confirmation à{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {email}
              </span>
              .
            </p>

            <button
              onClick={() => handleOpenChange(false)}
              className="mt-8 px-8 py-3 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <ProgressDots currentStep={step} totalSteps={3} />

            {/* Step 1: Contact Info */}
            <div
              className={`transition-opacity duration-300 ${
                step === 1
                  ? "opacity-100"
                  : "opacity-0 absolute pointer-events-none"
              }`}
            >
              {step === 1 && (
                <>
                  <div className="text-center mb-6">
                    <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      Parlons de votre projet
                    </DialogTitle>
                    <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                      Remplissez ce formulaire pour être contacté par notre
                      équipe.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="lead-firstname" className={labelClasses}>
                          Prénom <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="lead-firstname"
                          type="text"
                          className={inputClasses}
                          placeholder="Votre prénom"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div>
                        <label htmlFor="lead-lastname" className={labelClasses}>
                          Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="lead-lastname"
                          type="text"
                          className={inputClasses}
                          placeholder="Votre nom"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="lead-email" className={labelClasses}>
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="lead-email"
                        type="email"
                        className={inputClasses}
                        placeholder="votre@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="lead-phone" className={labelClasses}>
                        Téléphone <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="lead-phone"
                        type="tel"
                        className={inputClasses}
                        placeholder="+33 6 12 34 56 78"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="lead-source" className={labelClasses}>
                        Comment avez-vous entendu parler de nous ?{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <select
                          id="lead-source"
                          className={selectClasses}
                          value={source}
                          onChange={(e) => setSource(e.target.value)}
                        >
                          <option value="" disabled>
                            Sélectionnez une option
                          </option>
                          {SOURCE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <svg
                            className="h-4 w-4 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                      {source === "Autre" && (
                        <input
                          type="text"
                          className={`${inputClasses} mt-2`}
                          placeholder="Précisez..."
                          value={sourceOther}
                          onChange={(e) => setSourceOther(e.target.value)}
                        />
                      )}
                    </div>
                  </div>

                  <button
                    onClick={handleContinueStep1}
                    className="w-full mt-6 px-6 py-3 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors"
                  >
                    Continuer
                  </button>

                  <div className="flex items-center justify-center gap-4 mt-4">
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-grayDark-400">
                      <svg
                        className="w-3.5 h-3.5 text-green-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Sans engagement
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-grayDark-400">
                      <svg
                        className="w-3.5 h-3.5 text-green-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      100% gratuit
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Step 2: Motivations */}
            <div
              className={`transition-opacity duration-300 ${
                step === 2
                  ? "opacity-100"
                  : "opacity-0 absolute pointer-events-none"
              }`}
            >
              {step === 2 && (
                <>
                  <div className="text-center mb-6">
                    <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      Parlez-nous de vos motivations
                    </DialogTitle>
                    <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                      Ces informations nous permettent de mieux préparer notre
                      échange.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="lead-objective" className={labelClasses}>
                        Quel est votre objectif principal ?{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <select
                          id="lead-objective"
                          className={selectClasses}
                          value={objective}
                          onChange={(e) => setObjective(e.target.value)}
                        >
                          <option value="" disabled>
                            Sélectionnez un objectif
                          </option>
                          {OBJECTIVE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <svg
                            className="h-4 w-4 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                      {objective === "Autre" && (
                        <input
                          type="text"
                          className={`${inputClasses} mt-2`}
                          placeholder="Précisez votre objectif..."
                          value={objectiveOther}
                          onChange={(e) => setObjectiveOther(e.target.value)}
                        />
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="lead-motivation"
                        className={labelClasses}
                      >
                        Quelles sont vos motivations ?
                      </label>
                      <textarea
                        id="lead-motivation"
                        className={`${inputClasses} resize-none`}
                        rows={4}
                        placeholder="Dites-nous en quelques mots ce qui vous motive à rejoindre cette formation..."
                        value={motivation}
                        onChange={(e) => setMotivation(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setStep(1)}
                      className="px-4 py-3 rounded-lg text-sm font-medium text-gray-600 dark:text-grayDark-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-grayDark-800 transition-colors"
                    >
                      &larr; Retour
                    </button>
                    <button
                      onClick={handleContinueStep2}
                      className="flex-1 px-6 py-3 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors"
                    >
                      Continuer
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Step 3: Contact Preference */}
            <div
              className={`transition-opacity duration-300 ${
                step === 3
                  ? "opacity-100"
                  : "opacity-0 absolute pointer-events-none"
              }`}
            >
              {step === 3 && (
                <>
                  <div className="text-center mb-6">
                    <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      Comment souhaitez-vous être contacté ?
                    </DialogTitle>
                    <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                      {firstName}, choisissez l&apos;option qui vous convient le
                      mieux.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Card 1: Callback */}
                    <div className="rounded-xl border border-gray-200 dark:border-grayDark-700 p-5 flex flex-col items-center text-center hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                      <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                        <svg
                          className="w-6 h-6 text-blue-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                          />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                        Être rappelé rapidement
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-4 flex-1">
                        Notre équipe vous rappelle dans les plus brefs délais
                        pour discuter de votre projet.
                      </p>
                      <button
                        onClick={handleCallBack}
                        disabled={isSubmitting}
                        className="w-full px-4 py-2.5 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSubmitting ? "Envoi..." : "Être rappelé"}
                      </button>
                    </div>

                    {/* Card 2: Book a slot */}
                    <div className="rounded-xl border border-gray-200 dark:border-grayDark-700 p-5 flex flex-col items-center text-center hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                      <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                        <svg
                          className="w-6 h-6 text-blue-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
                          />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                        Réserver un créneau
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-4 flex-1">
                        Choisissez le créneau qui vous convient pour un appel de
                        30 minutes.
                      </p>
                      <button
                        onClick={handleBookSlot}
                        disabled={isSubmitting}
                        className="w-full px-4 py-2.5 rounded-lg border border-blue-500 text-blue-500 text-sm font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSubmitting ? "Envoi..." : "Choisir un créneau"}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4">
                    <button
                      onClick={() => setStep(2)}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-grayDark-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-grayDark-800 transition-colors"
                    >
                      &larr; Retour
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        <style jsx>{`
          @keyframes drawCheck {
            to {
              stroke-dashoffset: 0;
            }
          }
          @keyframes scaleIn {
            from {
              transform: scale(0);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
